import React, { useState, useEffect, useMemo } from 'react';
import { getUserBettingHistory } from '../lib/bettingApi';
import './WinLossHistory.css';

const WinLossHistory = ({ user }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'wins', 'losses', 'break-even'
  const [sortBy, setSortBy] = useState('date'); // 'date', 'payout'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'

  useEffect(() => {
    if (user) {
      fetchHistory();
    }
  }, [user]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const data = await getUserBettingHistory(user.id);
      setHistory(data);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics
  const stats = useMemo(() => {
    return {
      total: history.length,
      wins: history.filter(h => h.won === true).length,
      losses: history.filter(h => h.won === false && h.payoutDifference < 0).length,
      breakEven: history.filter(h => h.payoutDifference === 0).length,
      totalProfit: history.reduce((sum, h) => sum + (h.payoutDifference || 0), 0)
    };
  }, [history]);

  // Filter history based on active tab
  const filteredHistory = useMemo(() => {
    return history.filter(item => {
      if (activeTab === 'all') return true;
      if (activeTab === 'wins') return item.won === true;
      if (activeTab === 'losses') return item.won === false && item.payoutDifference < 0;
      if (activeTab === 'break-even') return item.payoutDifference === 0;
      return true;
    });
  }, [history, activeTab]);

  // Sort history
  const sortedHistory = useMemo(() => {
    return [...filteredHistory].sort((a, b) => {
      if (sortBy === 'date') {
        const dateA = new Date(a.endedAt);
        const dateB = new Date(b.endedAt);
        return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
      }
      if (sortBy === 'payout') {
        return sortOrder === 'desc' 
          ? b.payoutDifference - a.payoutDifference
          : a.payoutDifference - b.payoutDifference;
      }
      return 0;
    });
  }, [filteredHistory, sortBy, sortOrder]);

  if (loading) {
    return <div className="history-loading">Loading history...</div>;
  }

  if (history.length === 0) {
    return (
      <div className="history-empty">
        <p>No betting history yet. Place some bets to see your results here!</p>
      </div>
    );
  }

  return (
    <div className="win-loss-history">
      {/* Statistics Summary */}
      <div className="history-stats">
        <div className="stat-item">
          <span className="stat-label">Total Games</span>
          <span className="stat-value">{stats.total}</span>
        </div>
        <div className="stat-item stat-wins">
          <span className="stat-label">Wins</span>
          <span className="stat-value">{stats.wins}</span>
        </div>
        <div className="stat-item stat-losses">
          <span className="stat-label">Losses</span>
          <span className="stat-value">{stats.losses}</span>
        </div>
        <div className="stat-item stat-profit">
          <span className="stat-label">Total Profit</span>
          <span className="stat-value">
            ${stats.totalProfit >= 0 ? '+' : ''}{stats.totalProfit.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="history-tabs">
        <button
          className={`tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All ({stats.total})
        </button>
        <button
          className={`tab ${activeTab === 'wins' ? 'active' : ''}`}
          onClick={() => setActiveTab('wins')}
        >
          Wins ({stats.wins})
        </button>
        <button
          className={`tab ${activeTab === 'losses' ? 'active' : ''}`}
          onClick={() => setActiveTab('losses')}
        >
          Losses ({stats.losses})
        </button>
        <button
          className={`tab ${activeTab === 'break-even' ? 'active' : ''}`}
          onClick={() => setActiveTab('break-even')}
        >
          Break-even ({stats.breakEven})
        </button>
      </div>

      {/* Sort Controls */}
      <div className="history-sort">
        <label>Sort by:</label>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="date">Date</option>
          <option value="payout">Payout</option>
        </select>
        <button 
          className="sort-order-btn"
          onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
          title={sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
        >
          {sortOrder === 'desc' ? '↓' : '↑'}
        </button>
      </div>

      {/* History List */}
      <div className="history-list">
        {sortedHistory.map((item) => {
          const isWin = item.won === true;
          const isLoss = !isWin && item.payoutDifference < 0;
          const isBreakEven = item.payoutDifference === 0;

          return (
            <div key={item.game.id} className={`history-item ${isWin ? 'won' : isLoss ? 'lost' : 'break-even'}`}>
              <div className="history-item-main">
                <div className="history-game-title-wrapper">
                  {item.game.title_image && (
                    <img
                      src={item.game.title_image}
                      alt={item.game.title}
                      className="history-game-title-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <div className="history-game-title">{item.game.title}</div>
                </div>
                <div className={`history-status ${isWin ? 'status-won' : isLoss ? 'status-lost' : 'status-break-even'}`}>
                  {isWin ? 'Win' : isLoss ? 'Loss' : 'Break-even'}
                </div>
              </div>
              <div className="history-item-payout">
                <span className="history-payout-label">Payout:</span>
                <span className={`history-payout-value ${isWin ? 'positive' : isLoss ? 'negative' : 'neutral'}`}>
                  ${item.payoutDifference >= 0 ? '+' : ''}{item.payoutDifference.toFixed(2)}
                </span>
              </div>
              <div className="history-item-date">
                {new Date(item.endedAt).toLocaleDateString()}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WinLossHistory;

